import { type TeamMember } from "@fitness-app/data-models";
import { TeamMemberStatus } from "@fitness-app/data-models/entities/TeamMember";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { getTeamOwnerId } from "../../../helpers/getTeamOwnerId";
import { type AppThunk } from "../../../index";
import {
  addTrainerToTeam,
  removeTrainerFromTeamSync,
  subscribeToTeamFailed,
  subscribeToTeamPending,
  subscribeToTeamSuccess,
  updateTrainerInTeam,
} from "../reducer";
import { fetchTeamMembers } from "./fetchTeamMembers";
import { unsubscribeFromTeam } from "./unsubscribeFromTeam";
import { openRealtimeChannel } from "../../../helpers/openRealtimeChannel";
import { claimRealtimeSlot, ownsRealtimeSlot } from "../../../helpers/realtimeSlot";

export const subscribeToTeam =
  (isHeadCoach = false): AppThunk =>
  async (dispatch, getState, { auth, subscriptions, db }) => {
    const claimedSlot = claimRealtimeSlot(subscriptions, "team");

    try {
      dispatch(subscribeToTeamPending());

      await dispatch(fetchTeamMembers(isHeadCoach));

      const currentUser = await getLoggedUser(auth);
      const ownerId = getTeamOwnerId(currentUser);

      // a newer subscribe or an unsubscribe took the slot while we were fetching
      if (!ownsRealtimeSlot(subscriptions, "team", claimedSlot)) {
        return;
      }

      const channelName = `team-list-${currentUser.id}`;
      const channel = await openRealtimeChannel(db, channelName, subscriptions.realtimeTeamListChannel);

      // detaching the previous channel is another await - the slot may have changed hands
      if (!ownsRealtimeSlot(subscriptions, "team", claimedSlot)) {
        await db.removeChannel(channel);
        return;
      }

      subscriptions.teamListChannelName = channelName;
      subscriptions.realtimeTeamListChannel = channel;

      subscriptions.realtimeTeamListChannel
        .on<TeamMember>(
          "postgres_changes",
          { event: "*", schema: "public", table: "team_member", filter: `trainer=eq.${ownerId}` },
          (payload) => {
            if (payload.eventType === "UPDATE") {
              // Usunięcie jest teraz zmianą statusu, więc z listy znika po UPDATE,
              // nie po DELETE (TRE-1731).
              if (payload.new.status === TeamMemberStatus.DELETED) {
                dispatch(removeTrainerFromTeamSync(payload.new.id));
              } else {
                dispatch(updateTrainerInTeam(payload.new));
              }
            }
            if (payload.eventType === "INSERT") {
              dispatch(addTrainerToTeam(payload.new));
            }
            if (payload.eventType === "DELETE" && payload.old.id) {
              dispatch(removeTrainerFromTeamSync(payload.old.id));
            }
          },
        )
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            dispatch(subscribeToTeamSuccess());
          }
          if (status === "TIMED_OUT") {
            dispatch(unsubscribeFromTeam());
          }
        });
    } catch {
      dispatch(subscribeToTeamFailed());
    }
  };
